import { useEffect, useRef } from "react";

/**
 * Adds `is-visible` class once the element scrolls into view.
 * Honors prefers-reduced-motion.
 */
export const useReveal = <T extends HTMLElement = HTMLDivElement>(
  options: IntersectionObserverInit = { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      el.classList.add("is-visible");
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          el.classList.add("is-visible");
          obs.unobserve(el);
        }
      });
    }, options);
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ref;
};

type RevealProps = {
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  delay?: number;
  variant?: "up" | "fade" | "left" | "right" | "scale";
  children: React.ReactNode;
};

export const Reveal = ({
  as: Tag = "div",
  className = "",
  delay = 0,
  variant = "up",
  children,
}: RevealProps) => {
  const ref = useReveal<HTMLElement>();
  const Comp = Tag as any;
  return (
    <Comp
      ref={ref as any}
      className={`reveal reveal-${variant} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Comp>
  );
};
